import { Component, Input, OnDestroy, OnInit, SkipSelf } from '@angular/core';
import { ControlContainer, UntypedFormBuilder, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { SeriousIncidentType } from '@app/api-module/control-centre-api-client';
import { FormElementModel, seriousIncidentTypeAnz, seriousIncidentTypeUki } from '@app/clinical-feedback/clinical-feedback.model';
import { NewClinicalFeedbackFormHelper, ShowLogic } from '@app/clinical-feedback/new-clinical-feedback/new-clinical-feedback.helper';
import { CommonUtilities } from '@app/shared/common-utilities';
import { Subscription } from 'rxjs';
import { ClinicalFeedbackConstants } from 'src/app/clinical-feedback/clinical-feedback-constants';

@Component({
    selector: 'app-confirmed-serious-incident-form',
    templateUrl: './confirmed-serious-incident-form.component.html',
    viewProviders: [
        {
            provide: ControlContainer,
            useFactory: (container: ControlContainer) => container,
            deps: [[new SkipSelf(), ControlContainer]]
        }
    ]
})
export class ConfirmedSeriousIncidentFormComponent implements OnInit, OnDestroy {
    @Input()
    seriousIncidentType: SeriousIncidentType;
    formControlName = 'confirmedSeriousIncident';
    formGroup!: UntypedFormGroup;
    formElementModels = {
        hasDutyOfCandourMeetingBeenPlanned: new FormElementModel({
            id: 'hasDutyOfCandourMeetingBeenPlanned',
            label: 'Has a Duty of Candour meeting been planned or occurred?',
            fieldName: 'hasDutyOfCandourMeetingBeenPlanned'
        }),
        supportFromEverlight: new FormElementModel({
            id: 'supportFromEverlight',
            fieldName: 'SuppportFromEverlight',
            label: 'What do you need from us to support the meeting (from Everlight/Reporting radiologist)',
            placeholder: 'Add text'
        }),
        lessonsLearnt: new FormElementModel({
            id: 'lessonsLearnt',
            fieldName: 'LessonsLearnt',
            label: 'Are there any lessons learnt for Everlight/Reporting Radiologist from the Duty of Candour meeting?',
            placeholder: 'Add text'
        })
    };

    showDutyOfCandourDetails = false;
    private readonly subscriptions = new Subscription();

    clinicalFeedbackConstants = ClinicalFeedbackConstants;

    constructor(public parent: FormGroupDirective, private readonly formBuilder: UntypedFormBuilder) {}

    ngOnInit(): void {
        this.formGroup = this.formBuilder.group({
            hasDutyOfCandourMeetingBeenPlanned: [null, [Validators.required]],
            dateOfMeeting: [{ value: null, disabled: true }, [Validators.required]],
            supportFromEverlight: [{ value: '', disabled: true }],
            lessonsLearnt: [{ value: '', disabled: true }]
        });
        (this.parent.form.get('advisedSeriousIncident') as UntypedFormGroup).addControl(this.formControlName, this.formGroup);
        this.handleValueChanges();
    }

    public getSeriousIncidentTypeTitle(): string {
        switch (this.seriousIncidentType) {
            case SeriousIncidentType.AimsIms:
                return CommonUtilities.getKeyValuePairData(seriousIncidentTypeAnz, this.seriousIncidentType);
            case SeriousIncidentType.ConfirmedSeriousIncident:
                return CommonUtilities.getKeyValuePairData(seriousIncidentTypeUki, this.seriousIncidentType);
            default:
                throw Error(`Unexpected Serious Incident Type ${this.seriousIncidentType}`);
        }
    }

    private handleValueChanges() {
        this.subscriptions.add(
            NewClinicalFeedbackFormHelper.SetVisibilityOfControlsOnBooleanEvent(
                this.formGroup.controls['hasDutyOfCandourMeetingBeenPlanned'].valueChanges,
                [
                    this.formGroup.controls['dateOfMeeting'],
                    this.formGroup.controls['supportFromEverlight'],
                    this.formGroup.controls['lessonsLearnt']
                ],
                visibility => (this.showDutyOfCandourDetails = visibility),
                ShowLogic.ShowOnYes
            )
        );
    }

    ngOnDestroy() {
        //if a formGroup already exists in the parent when ngOnInit runs, new formGroup wouldn't work properly
        (this.parent.form.get('advisedSeriousIncident') as UntypedFormGroup).removeControl(this.formControlName);
        this.subscriptions.unsubscribe();
    }
}
